import QuestionView from './base/question-view';
import MediaCaptureManager from '../media-capture-manager';
import Utils from '../../utils';
import UploadProgress from '../controls/upload-progress';

export default class AudioUploadQuestionView extends QuestionView {
    /**
     * @param {AudioUploadQuestion} question
     * @param {QuestionViewSettings} settings
     * @param {MediaCaptureManager} mediaCaptureManager
     */
    constructor(
        question,
        settings,
        mediaCaptureManager = new MediaCaptureManager({ audio: true }, ['audio/mp4', 'audio/webm'])
    ) {
        super(question, settings);

        this._mediaCaptureManager = mediaCaptureManager;
        this._progressControl = new UploadProgress(this._container.find('.circular-progress'));

        this._minDurationInSeconds = this._question.minDurationInSeconds;
        this._maxDurationInSeconds = this._question.maxDurationInSeconds;
        if (!this._maxDurationInSeconds || this._maxDurationInSeconds > 900) {
            this._maxDurationInSeconds = 900;
        }

        this._recordingDurationInSeconds = 0;

        this._init();
        this._attachHandlers();
    }

    _init() {
        this._baseSelector = 'cf-audio-upload';

        this._node = this._container.find('.cf-media-upload');

        this._mediaUploadControls = this._node.find('.cf-media-upload__controls');
        this._mediaUploadButton = this._node.find('.cf-media-upload__upload-button');
        this._playUploadedAudioButton = this._node.find('.cf-audio-preview__icon');

        this._recordedAudioPlayer = this._node.find(`.${this._baseSelector}__record`)[0];

        this._audioRecording = this._node.find(`.${this._baseSelector}`);

        this._recordingDurationLabel = this._node.find(`.${this._baseSelector}__duration-label`);
        this._updateRecordingDurationLabel();

        this._closeButton = this._node.find(`.${this._baseSelector}__close-button`);
        this._recordButton = this._node.find(`.${this._baseSelector}__record-button`);
        this._pauseButton = this._node.find(`.${this._baseSelector}__pause-button`);
        this._resumeButton = this._node.find(`.${this._baseSelector}__resume-button`);
        this._stopButton = this._node.find(`.${this._baseSelector}__stop-button`);
        this._retakeButton = this._node.find(`.${this._baseSelector}__retake-button`);
        this._doneButton = this._node.find(`.${this._baseSelector}__done-button`);
        this._retryUploadButton = this._node.find(`.${this._baseSelector}__retry-button`);

        this._errorMessage = this._node.find(`.${this._baseSelector}__error-message`);
        this._errorDetails = this._node.find(`.${this._baseSelector}__error-details`);

        this._uploadedAudioPreview = this._node.find('.cf-audio-preview');

        this._uploadedAudioPlayer = this._node.find('.cf-audio-preview__audio-player')[0];
        this._uploadedAudioDurationLabel = this._node.find('.cf-audio-preview__duration-label');

        this._closeUploadedAudioButton = this._node.find(`.cf-audio-preview__back-button`);

        this._deviceSettings = this._node.find(`.${this._baseSelector}__settings`);
        this._microphoneDeviceList = this._deviceSettings.find(`.${this._baseSelector}__microphone-list`);

        this._settingsButton = this._node.find(`.${this._baseSelector}__settings-button`);
        this._closeSettingsButton = this._node.find(`.${this._baseSelector}__close-settings-button`);
    }

    _attachHandlers() {
        this._mediaUploadButton.on('click', this._upload.bind(this));
        this._playUploadedAudioButton.on('click', this._play.bind(this));

        this._closeButton.on('click', this._reset.bind(this));
        this._recordButton.on('click', this._startRecording.bind(this));
        this._pauseButton.on('click', this._pauseRecording.bind(this));
        this._resumeButton.on('click', this._resumeRecording.bind(this));
        this._stopButton.on('click', this._stopRecording.bind(this));
        this._retakeButton.on('click', this._retake.bind(this));
        this._doneButton.on('click', this._startUploading.bind(this));
        this._retryUploadButton.on('click', this._startUploading.bind(this));

        this._settingsButton.on('click', this._toggleDeviceSettingsDisplay.bind(this));
        this._closeSettingsButton.on('click', this._closeDeviceSettings.bind(this));

        this._closeUploadedAudioButton.on('click', this._closeUploadedAudio.bind(this));

        this._mediaCaptureManager.capturingStartedEvent.on(this._onCapturingStarted.bind(this));
        this._mediaCaptureManager.mediaDevicesEnumeratedEvent.on(this._onMediaDevicesEnumerated.bind(this));
        this._mediaCaptureManager.mediaDeviceSelectionChangedEvent.on(this._onMediaDeviceSelectionChanged.bind(this));
        this._mediaCaptureManager.recordingStartedEvent.on(this._onRecordingStarted.bind(this));
        this._mediaCaptureManager.recordingDurationUpdatedEvent.on(this._onRecordingDurationUpdated.bind(this));
        this._mediaCaptureManager.recordingPausedEvent.on(this._onRecordingPaused.bind(this));
        this._mediaCaptureManager.recordingResumedEvent.on(this._onRecordingResumed.bind(this));
        this._mediaCaptureManager.recordedDataAvailableEvent.on(this._onRecordedDataAvailable.bind(this));
        this._mediaCaptureManager.retakeEvent.on(this._onRetake.bind(this));
        this._mediaCaptureManager.resetEvent.on(this._onReset.bind(this));
        this._mediaCaptureManager.errorEvent.on(this._onError.bind(this));

        this._question.uploader.uploadStartEvent.on(this._onUploadingStarted.bind(this));
        this._question.uploader.uploadProgressEvent.on(this._onUploadingProgress.bind(this));
        this._question.uploader.uploadCompleteEvent.on(this._onUploadingCompleted.bind(this));
        this._question.uploader.uploadCancelEvent.on(this._onlUploadingCanceled.bind(this));
        this._question.uploader.uploadErrorEvent.on(this._onUploadingError.bind(this));

        this._progressControl.cancelEvent.on(this._cancelUploading.bind(this));
    }

    _setState(state = null) {
        this._audioRecording.removeClass(`${this._baseSelector}--initializing`);
        this._audioRecording.removeClass(`${this._baseSelector}--ready`);
        this._audioRecording.removeClass(`${this._baseSelector}--recording`);
        this._audioRecording.removeClass(`${this._baseSelector}--paused`);
        this._audioRecording.removeClass(`${this._baseSelector}--recorded`);
        this._audioRecording.removeClass(`${this._baseSelector}--uploading`);
        this._audioRecording.removeClass(`${this._baseSelector}--error`);
        this._audioRecording.removeClass(`${this._baseSelector}--upload-error`);

        if (state !== null) {
            this._audioRecording.addClass(`${this._baseSelector}--${state}`);
        }

        if (state !== 'ready') {
            this._deviceSettings.addClass(`${this._baseSelector}__settings--hidden`);
        }
    }

    /*
       DO NOT remove or change the method
       it is overridden in mobile apps (native camera experience is used instead)
   */
    _upload() {
        if (!Utils.isEmpty(this._question.value.previewUrl)) {
            if (!confirm(this._settings.messages.retakeAudio)) {
                return;
            }
            this._question.setValue('', '', '');
        }

        this._openRecording();
    }

    _openRecording() {
        this._node.addClass('cf-media-upload--recording');
        this._node.removeClass('cf-media-upload--uploaded');
        this._setState('initializing');

        this._mediaCaptureManager.startCapturing();
    }

    _reset() {
        this._mediaCaptureManager.reset();
    }

    _onReset() {
        this._recordingDurationInSeconds = 0;
        this._updateRecordingDurationLabel();

        this._setState();
        this._node.removeClass('cf-media-upload--recording');
        this._mediaUploadControls.removeClass('cf-media-upload__controls--hidden');
    }

    _onCapturingStarted() {
        this._setState('ready');
    }

    _onMediaDevicesEnumerated() {
        this._microphoneDeviceList.html('');
        this._mediaCaptureManager.microphoneList.forEach((microphoneDevice) => {
            this._renderMicrophoneDeviceButton(microphoneDevice);
        });

        if (this._mediaCaptureManager.selectedMicrophone) {
            this._selectMicrophone(this._mediaCaptureManager.selectedMicrophone);
        }

        this._mediaCaptureManager.stopCapturing();
    }

    _renderMicrophoneDeviceButton(microphoneDevice) {
        this._microphoneDeviceList.append(`<div id="${this._getMicrophoneButtonId(microphoneDevice.deviceId)}" class="${
            this._baseSelector
        }__device">
    <div class="${this._baseSelector}__device-selected-icon"></div>
    <span class="${this._baseSelector}__device-label">${microphoneDevice.label}</span>
</div>`);

        this._microphoneDeviceList.find(`#${this._getMicrophoneButtonId(microphoneDevice.deviceId)}`).click(() => {
            this._onMicrophoneDeviceButtonClick(microphoneDevice);
        });
    }

    _onMicrophoneDeviceButtonClick(microphoneDevice) {
        if (
            this._mediaCaptureManager.selectedMicrophone &&
            this._mediaCaptureManager.selectedMicrophone.deviceId === microphoneDevice.deviceId
        ) {
            return;
        }

        this._mediaCaptureManager.selectMicrophone(microphoneDevice);
    }

    _getMicrophoneButtonId(microphoneDeviceId) {
        return `microphone-${microphoneDeviceId.replace(/[^a-zA-Z0-9]/gi, '')}`;
    }

    _onMediaDeviceSelectionChanged() {
        if (this._mediaCaptureManager.selectedMicrophone) {
            this._selectMicrophone(this._mediaCaptureManager.selectedMicrophone);
        }
    }

    _selectMicrophone(microphoneDevice) {
        this._microphoneDeviceList.children().each((index, device) => {
            device.classList.remove(`${this._baseSelector}__device--selected`);
        });

        this._microphoneDeviceList
            .find(`#${this._getMicrophoneButtonId(microphoneDevice.deviceId)}`)
            .addClass(`${this._baseSelector}__device--selected`);
    }

    _toggleDeviceSettingsDisplay() {
        this._deviceSettings.toggleClass(`${this._baseSelector}__settings--hidden`);
    }

    _closeDeviceSettings() {
        this._deviceSettings.addClass(`${this._baseSelector}__settings--hidden`);
    }

    _startRecording() {
        this._mediaCaptureManager.startRecording();
    }

    _onRecordingStarted() {
        this._setState('recording');
    }

    _onRecordingDurationUpdated(recordingDurationInMilliseconds) {
        this._recordingDurationInSeconds = Math.floor(recordingDurationInMilliseconds / 1000);
        this._updateRecordingDurationLabel();

        if (this._minDurationInSeconds && this._recordingDurationInSeconds < this._minDurationInSeconds) {
            this._audioRecording.addClass(`${this._baseSelector}--min-duration-not-reached`);
        } else {
            this._audioRecording.removeClass(`${this._baseSelector}--min-duration-not-reached`);
        }

        if (this._maxDurationInSeconds && this._recordingDurationInSeconds >= this._maxDurationInSeconds) {
            this._stopRecording();
        }
    }

    _updateRecordingDurationLabel() {
        this._recordingDurationLabel.text(
            `${Utils.formatDuration(this._recordingDurationInSeconds)} / ${Utils.formatDuration(
                this._maxDurationInSeconds
            )}`
        );
    }

    _pauseRecording() {
        this._mediaCaptureManager.pauseRecording();
    }

    _onRecordingPaused() {
        this._setState('paused');
    }

    _resumeRecording() {
        this._mediaCaptureManager.resumeRecording();
    }

    _onRecordingResumed() {
        this._setState('recording');
    }

    _stopRecording() {
        if (this._minDurationInSeconds && this._recordingDurationInSeconds < this._minDurationInSeconds) {
            return;
        }

        this._mediaCaptureManager.stopRecording();
    }

    _onRecordedDataAvailable() {
        this._recordedAudioPlayer.src = URL.createObjectURL(this._mediaCaptureManager.recordedDataBlob);
        this._setState('recorded');
    }

    _retake() {
        if (!confirm(this._settings.messages.retakeAudio)) {
            return;
        }

        this._recordingDurationInSeconds = 0;
        this._mediaCaptureManager.retake();
    }

    _onRetake() {
        this._updateRecordingDurationLabel();

        this._setState('initializing');
    }

    _startUploading() {
        try {
            this._question.uploader.upload(
                this._mediaCaptureManager.recordedDataBlob,
                this._recordingDurationInSeconds
            );
        } catch (error) {
            this._showError(error.message);
        }
    }

    _onUploadingStarted() {
        this._progressControl.start();

        this._setState('uploading');
        this._recordedAudioPlayer.pause();
        this.pending = true;
    }

    _onUploadingProgress(value) {
        this._progressControl.progress(value);
    }

    _onUploadingCompleted({ mediaFileId, mediaFileUrl }) {
        this._question.setValue(mediaFileId, mediaFileUrl, Utils.formatDuration(this._recordingDurationInSeconds));
        this._mediaCaptureManager.reset();

        this.pending = false;
    }

    _cancelUploading() {
        try {
            this._question.uploader.cancel();
        } catch (error) {
            this._showError(error.message);
        }
    }

    _onlUploadingCanceled() {
        this._setState('recorded');

        this.pending = false;
    }

    _onUploadingError(errorMessage) {
        this._showError(errorMessage);
        this._setState('upload-error');

        this.pending = false;
    }

    _onError(error) {
        this._showError(error.message);
        this._setState('error');
    }

    _showError(errorDetails) {
        this._errorMessage.text(this._settings.messages.error);
        this._errorDetails.text(errorDetails);
    }

    async _onModelValueChange() {
        if (!this._question.value.audioId) {
            return;
        }

        this._updatePreview();

        this._node.addClass('cf-media-upload--uploaded');
    }

    _updatePreview() {
        this._uploadedAudioPlayer.src = this._question.value.previewUrl;
        this._uploadedAudioDurationLabel.text(this._question.value.duration);
    }

    /*
        DO NOT remove or change the method
        it is overridden in mobile apps (native camera experience is used instead)
    */
    _play() {
        this._node.addClass('cf-media-upload--playing');
        this._uploadedAudioPreview.addClass('cf-audio-preview--playing');
        this._uploadedAudioPlayer.play();
    }

    _closeUploadedAudio() {
        this._uploadedAudioPlayer.pause();
        this._uploadedAudioPlayer.currentTime = 0;
        this._node.removeClass('cf-media-upload--playing');
        this._uploadedAudioPreview.removeClass('cf-audio-preview--playing');
    }
}
